import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import api from "@/app/api";
import { superAdminKeys } from "../keys/superadmin";
import type {
  SuperAdminCompanyCreate,
  SuperAdminCompanyUpdate,
  SuperAdminUserCreate,
  SuperAdminUserUpdate,
  SuperAdminEmployeeCreate,
  SuperAdminEmployeeUpdate,
  ListCompaniesParams,
  ListUsersParams,
  ListEmployeesParams,
  BulkFeatureFlagUpdate,
  SuperAdminGroupCreate,
  SuperAdminGroupRename,
  ListGroupsParams,
  MessagingChannelType,
  ChannelAssignmentUpdate,
  EmailChannelCreate,
  SmsChannelCreate,
} from "@/app/api/types/superadmin";
import type { GroupResponse } from "@/app/api/types/group";

// --- Stats ---

export function useSuperAdminStats() {
  return useQuery({
    queryKey: superAdminKeys.stats(),
    queryFn: () => api.superAdmin.getStats(),
    staleTime: 60 * 1000, // 1 minute
  });
}

// --- Companies ---

export function useSuperAdminCompanies(params: ListCompaniesParams = {}) {
  return useQuery({
    queryKey: superAdminKeys.companiesList(params),
    queryFn: () => api.superAdmin.listCompanies(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Scroll-paginated company picker source. Accumulates pages so the combobox can
 * load more on scroll (and report "showing N of total"), instead of stopping at
 * the first capped page. A changing `search` term swaps the query key, resetting
 * back to page 1. `page` is the cursor, so it is omitted from the params.
 */
export function useSuperAdminCompaniesInfinite(
  params: Omit<ListCompaniesParams, "page"> = {},
) {
  return useInfiniteQuery({
    queryKey: superAdminKeys.companiesInfinite(params),
    queryFn: ({ pageParam }) =>
      api.superAdmin.listCompanies({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? lastPageParam + 1 : undefined;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useSuperAdminCompany(id: string | null) {
  return useQuery({
    queryKey: superAdminKeys.company(id!),
    queryFn: () => api.superAdmin.getCompany(id!),
    enabled: !!id,
  });
}

export function useCreateSuperAdminCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SuperAdminCompanyCreate) =>
      api.superAdmin.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.companies() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.stats() });
    },
  });
}

export function useUpdateSuperAdminCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SuperAdminCompanyUpdate }) =>
      api.superAdmin.updateCompany(id, data),
    onSuccess: (updatedCompany) => {
      queryClient.setQueryData(
        superAdminKeys.company(updatedCompany.id),
        updatedCompany,
      );
      queryClient.invalidateQueries({ queryKey: superAdminKeys.companies() });
    },
  });
}

export function useDeleteSuperAdminCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.superAdmin.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.companies() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.stats() });
    },
  });
}

// --- Users ---

export function useSuperAdminUsers(params: ListUsersParams = {}) {
  return useQuery({
    queryKey: superAdminKeys.usersList(params),
    queryFn: () => api.superAdmin.listUsers(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Scroll-paginated user picker source. Accumulates pages so the combobox can
 * load more on scroll (and report "showing N of total"), instead of stopping at
 * the first capped page. A changing `search` (or `company_id`) swaps the query
 * key, resetting back to page 1. `page` is the cursor, so it is omitted.
 */
export function useSuperAdminUsersInfinite(
  params: Omit<ListUsersParams, "page"> = {},
) {
  return useInfiniteQuery({
    queryKey: superAdminKeys.usersInfinite(params),
    queryFn: ({ pageParam }) =>
      api.superAdmin.listUsers({ ...params, page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? lastPageParam + 1 : undefined;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useSuperAdminUser(id: string | null) {
  return useQuery({
    queryKey: superAdminKeys.user(id!),
    queryFn: () => api.superAdmin.getUser(id!),
    enabled: !!id,
  });
}

export function useCreateSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SuperAdminUserCreate) => api.superAdmin.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.users() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.stats() });
    },
  });
}

export function useUpdateSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SuperAdminUserUpdate }) =>
      api.superAdmin.updateUser(id, data),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(
        superAdminKeys.user(updatedUser.id),
        updatedUser,
      );
      queryClient.invalidateQueries({ queryKey: superAdminKeys.users() });
    },
  });
}

export function useDeleteSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.superAdmin.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.users() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.stats() });
    },
  });
}

/**
 * Reactivate a soft-deleted user — the inverse of `useDeleteSuperAdminUser`.
 * Flips `is_active` back on via the generic user PATCH so the account can log
 * in again, without going through Adminer.
 */
export function useReactivateSuperAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      api.superAdmin.updateUser(id, { is_active: true }),
    onSuccess: (updatedUser) => {
      queryClient.setQueryData(
        superAdminKeys.user(updatedUser.id),
        updatedUser,
      );
      queryClient.invalidateQueries({ queryKey: superAdminKeys.users() });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.stats() });
    },
  });
}

// --- Employees ---

export function useSuperAdminEmployees(params: ListEmployeesParams = {}) {
  return useQuery({
    queryKey: superAdminKeys.employeesList(params),
    queryFn: () => api.superAdmin.listEmployees(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useCreateSuperAdminEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SuperAdminEmployeeCreate) =>
      api.superAdmin.createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.employees() });
    },
  });
}

export function useUpdateSuperAdminEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: SuperAdminEmployeeUpdate;
    }) => api.superAdmin.updateEmployee(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.employees() });
    },
  });
}

export function useDeleteSuperAdminEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.superAdmin.deleteEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.employees() });
    },
  });
}

export function useLinkSuperAdminEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      api.superAdmin.linkEmployee(id, { user_id: userId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.employees() });
    },
  });
}

export function useUnlinkSuperAdminEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.superAdmin.unlinkEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.employees() });
    },
  });
}

// --- Impersonation ---

export function useImpersonateUser() {
  return useMutation({
    mutationFn: (userId: string) => api.superAdmin.impersonateUser(userId),
  });
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: (userId: string) => api.superAdmin.resetUserPassword(userId),
  });
}

export function useSetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      api.superAdmin.setUserPassword(userId, password),
  });
}

// --- Feature Flags ---

export function useSuperAdminFeatureFlags() {
  return useQuery({
    queryKey: superAdminKeys.featureFlags(),
    queryFn: () => api.superAdmin.listFeatureFlags(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

export function useUpdateSuperAdminCompanyFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      data,
    }: {
      companyId: string;
      data: BulkFeatureFlagUpdate;
    }) => api.superAdmin.updateCompanyFlags(companyId, data),
    onSuccess: (_, { companyId }) => {
      queryClient.invalidateQueries({
        queryKey: superAdminKeys.company(companyId),
      });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.companies() });
    },
  });
}

export function useUpdateSuperAdminUserFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: string;
      data: BulkFeatureFlagUpdate;
    }) => api.superAdmin.updateUserFlags(userId, data),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: superAdminKeys.user(userId) });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.users() });
    },
  });
}

// --- Company Users (full list for pickers) ---

/**
 * Every active user of one company, across all pages — the group owner/member
 * pickers must offer the whole roster, and listUsers caps `size` at 100. Page 1
 * establishes `total`; the rest are fetched in parallel and concatenated (no
 * silent cap).
 */
export function useAllSuperAdminCompanyUsers(companyId: string | null) {
  const SIZE = 100;
  return useQuery({
    queryKey: superAdminKeys.usersList({
      company_id: companyId ?? undefined,
      size: SIZE,
      page: 0, // sentinel: "all pages" variant, distinct from paged lists
    }),
    queryFn: async () => {
      const params = { company_id: companyId!, size: SIZE };
      const first = await api.superAdmin.listUsers({ page: 1, ...params });
      const pageCount = Math.max(1, Math.ceil(first.total / SIZE));
      if (pageCount === 1) return first.items;
      const rest = await Promise.all(
        Array.from({ length: pageCount - 1 }, (_, i) =>
          api.superAdmin.listUsers({ page: i + 2, ...params }),
        ),
      );
      return [first.items, ...rest.map((p) => p.items)].flat();
    },
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });
}

// --- Groups (cross-company browse) ---

export function useSuperAdminGroupsList(params: ListGroupsParams = {}) {
  return useQuery({
    queryKey: superAdminKeys.groupsList(params),
    queryFn: () => api.superAdmin.listGroups(params),
    staleTime: 30 * 1000,
  });
}

// --- Groups (per-company) ---

export function useSuperAdminCompanyGroups(companyId: string | null) {
  return useQuery({
    queryKey: superAdminKeys.groups(companyId!),
    queryFn: () => api.superAdmin.listCompanyGroups(companyId!),
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });
}

export function useSuperAdminCompanyGroup(
  companyId: string | null,
  groupId: string | null,
) {
  return useQuery({
    queryKey: superAdminKeys.group(companyId!, groupId!),
    queryFn: () => api.superAdmin.getCompanyGroup(companyId!, groupId!),
    enabled: !!companyId && !!groupId,
  });
}

/**
 * Shared cache-update for a mutation that returns the settled group: refresh the
 * detail entry and invalidate the company's group list.
 */
function useGroupMutationSync(companyId: string) {
  const queryClient = useQueryClient();
  return (group: GroupResponse) => {
    queryClient.setQueryData(superAdminKeys.group(companyId, group.id), group);
    queryClient.invalidateQueries({
      queryKey: superAdminKeys.groups(companyId),
    });
    // Keep the cross-company Groups tab in sync too.
    queryClient.invalidateQueries({ queryKey: superAdminKeys.groupsAll() });
  };
}

export function useCreateSuperAdminGroup(companyId: string) {
  const sync = useGroupMutationSync(companyId);
  return useMutation({
    mutationFn: (data: SuperAdminGroupCreate) =>
      api.superAdmin.createCompanyGroup(companyId, data),
    onSuccess: sync,
  });
}

export function useRenameSuperAdminGroup(companyId: string) {
  const sync = useGroupMutationSync(companyId);
  return useMutation({
    mutationFn: ({
      groupId,
      data,
    }: {
      groupId: string;
      data: SuperAdminGroupRename;
    }) => api.superAdmin.renameCompanyGroup(companyId, groupId, data),
    onSuccess: sync,
  });
}

export function useDeleteSuperAdminGroup(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupId: string) =>
      api.superAdmin.deleteCompanyGroup(companyId, groupId),
    onSuccess: (_data, groupId) => {
      queryClient.removeQueries({
        queryKey: superAdminKeys.group(companyId, groupId),
      });
      queryClient.invalidateQueries({
        queryKey: superAdminKeys.groups(companyId),
      });
      queryClient.invalidateQueries({ queryKey: superAdminKeys.groupsAll() });
    },
  });
}

export function useAddSuperAdminGroupMember(companyId: string) {
  const sync = useGroupMutationSync(companyId);
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.superAdmin.addCompanyGroupMember(companyId, groupId, userId),
    onSuccess: sync,
  });
}

export function useRemoveSuperAdminGroupMember(companyId: string) {
  const sync = useGroupMutationSync(companyId);
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.superAdmin.removeCompanyGroupMember(companyId, groupId, userId),
    onSuccess: sync,
  });
}

export function useUpdateSuperAdminGroupMemberRole(companyId: string) {
  const sync = useGroupMutationSync(companyId);
  return useMutation({
    mutationFn: ({
      groupId,
      userId,
      role,
    }: {
      groupId: string;
      userId: string;
      role: 0 | 1;
    }) =>
      api.superAdmin.updateCompanyGroupMemberRole(
        companyId,
        groupId,
        userId,
        role,
      ),
    onSuccess: sync,
  });
}

export function useSetSuperAdminGroupOwner(companyId: string) {
  const sync = useGroupMutationSync(companyId);
  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      api.superAdmin.setCompanyGroupOwner(companyId, groupId, userId),
    onSuccess: sync,
  });
}

// --- Messaging Channels ---

export function useSuperAdminMessagingChannels(companyId: string | null) {
  return useQuery({
    queryKey: superAdminKeys.messagingChannels(companyId!),
    queryFn: () => api.superAdmin.getCompanyMessagingChannels(companyId!),
    enabled: !!companyId,
    staleTime: 30 * 1000,
  });
}

/**
 * Reassign a channel's owner and/or link/unlink it to a group. On success the
 * company's channel list is invalidated so the drilldown reflects the new
 * assignment.
 */
export function useUpdateMessagingChannelAssignment(companyId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      channelType,
      channelId,
      data,
    }: {
      channelType: MessagingChannelType;
      channelId: string;
      data: ChannelAssignmentUpdate;
    }) =>
      api.superAdmin.updateMessagingChannelAssignment(
        companyId,
        channelType,
        channelId,
        data,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: superAdminKeys.messagingChannels(companyId),
      });
    },
  });
}

/** Shared cache refresh for channel create/delete: reload the company list. */
function useMessagingChannelInvalidate(companyId: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: superAdminKeys.messagingChannels(companyId),
    });
}

export function useCreateEmailChannel(companyId: string) {
  const invalidate = useMessagingChannelInvalidate(companyId);
  return useMutation({
    mutationFn: (data: EmailChannelCreate) =>
      api.superAdmin.createEmailChannel(companyId, data),
    onSuccess: invalidate,
  });
}

export function useCreateSmsChannel(companyId: string) {
  const invalidate = useMessagingChannelInvalidate(companyId);
  return useMutation({
    mutationFn: (data: SmsChannelCreate) =>
      api.superAdmin.createSmsChannel(companyId, data),
    onSuccess: invalidate,
  });
}

export function useDeleteMessagingChannel(companyId: string) {
  const invalidate = useMessagingChannelInvalidate(companyId);
  return useMutation({
    mutationFn: ({
      channelType,
      channelId,
    }: {
      channelType: MessagingChannelType;
      channelId: string;
    }) =>
      api.superAdmin.deleteMessagingChannel(companyId, channelType, channelId),
    onSuccess: invalidate,
  });
}
